<template>
  <v-card flat>
    <!-- Datatable Filter -->
    <v-card-title>
      <v-layout row wrap>
        <v-flex sm6>
          <v-text-field append-icon="filter_list" :label="messages.label.PROVIDER_FILTER" single-line hide-details
            clearable v-model="search" @input="value => filterProviderList()">
          </v-text-field>
        </v-flex>
        <v-flex sm2>
        </v-flex>
        <v-flex sm4>
          <v-autocomplete v-model="selectedProviderGroups" :label="messages.label.PROVIDER_GROUP" multiple
            item-text="title" item-value="title" :items="providerGroups" return-object @change="retrieveProviderList()">
            <template v-slot:no-data>
              <v-list-tile>
                <v-list-tile-title>
                  No Provider groups in
                  <strong>{{ mpxParentAccount.title }}</strong>
                </v-list-tile-title>
              </v-list-tile>
            </template>
          </v-autocomplete>
        </v-flex>
      </v-layout>
    </v-card-title>
    <!-- Data Table -->
    <v-card-text>
      <v-data-table :headers="headers" :items="filteredProviderList" :pagination.sync="pagination"
        :total-items="totalItems" :loading="providersLoading" :rows-per-page-items="rowsPerPagesArray">
        <!-- Items -->
        <template slot="items" slot-scope="props">
          <tr @click="showCurrentProviderDetails(props.item)" :active="!!selected && selected.id == props.item.id"
            class="pointer">
            <td class="text-xs-left">{{ props.item.title }}</td>
            <td class="text-xs-left">{{ getAlternateNamesString(props.item.alternateNames) }}</td>
            <td class="text-xs-left">{{ props.item._providerGroupTitle }}</td>
          </tr>
        </template>
        <!-- No Data -->
        <template slot="no-data">
          <v-alert :value="true" color="info" class="black--text" icon="info">
            {{ messages.info.NO_PROVIDERS }}
          </v-alert>
        </template>
        <!-- No Results -->
        <template slot="no-results">
          <v-alert :value="true" color="info" class="black--text" icon="info">
            {{ messages.info.NO_FILTER_PROVIDERS }}
          </v-alert>
        </template>
        <!-- Page Text -->
        <template slot="pageText" slot-scope="props">
          {{ props.pageStart }} - {{ props.pageStop }} of {{ props.itemsLength }}
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex"
import ProvidersService from "@/Providers/ProvidersService"
import AppService from "@/AppService"
import messages from "@/Providers/messages.json"
import { GET_PROVIDER_GROUPS } from "@/store/constants/action-types"
import * as providersDataService from "@/Providers/ProvidersDataService"
import { VOD_SYCN_PREINGEST_DATA_SERVICE_URL } from "@/store/constants/getter-types"

/**
 * initialData
 */
function initialData(instance) {
  return {
    search: "",
    selected: {},
    providersLoading: false,
    pagination: {
      sortBy: "",
      page: 1,
      rowsPerPage: 25,
      descending: false
    },
    totalItems: 0,
    rowsPerPagesArray: [25, 50, 75, 100],
    headers: instance.getTableHeaders(),
    providersList: [],
    filteredProviderList: [],
    providerGroupsList: [],
    selectedProviderGroups: [],
    providerListObject: {},
    providerGroupQueryObject: {}
  }
}

export default {
  name: "ProvidersList",
  data: function () {
    return initialData(this)
  },
  computed: {
    ...mapState([
      "providerGroups", "mpxSubAccounts", "mpxParentAccount"
    ]),
    ...mapGetters([
      VOD_SYCN_PREINGEST_DATA_SERVICE_URL
    ])
  },
  watch: {
    /**
     * Watch handler for pagination object
     */
    pagination: {
      handler(newVal, oldVal) {
        if (oldVal !== newVal) {
          this.retrieveProviderList()
        }
      },
      deep: true
    }
  },
  methods: {
    ...mapActions([
      GET_PROVIDER_GROUPS
    ]),

    /**
     * reset
     */
    reset() {
      this.providersLoading = true
      this.GET_PROVIDER_GROUPS().then(() => {
        Object.assign(this.$data, initialData(this))
      })
    },

    /**
    * getPageParams - Construct pagination param
    * @param  {object} pagination pagination object
    * @return {string}            ex: "1-10"
    */
    getPageParams(pagination) {
      let startRange
      let endRange
      if (pagination.page === 1) {
        startRange = 1
        endRange = pagination.rowsPerPage
      } else {
        startRange = (pagination.page - 1) * pagination.rowsPerPage + 1
        endRange = startRange + pagination.rowsPerPage - 1
      }
      return startRange + "-" + endRange
    },

    /**
     * getSortParams - Construct sorting param
     *
     * @param  {object} pagination pagination object
     * @return {string}            ex: '<filedName>|desc'
     */
    getSortParams(pagination) {
      let sortParams = ""
      sortParams =
        !this.$lodash.isNull(pagination.sortBy) &&
          !this.$lodash.isEmpty(pagination.sortBy)
          ? pagination.sortBy
          : "updated|desc"
      if (pagination.descending) {
        sortParams += "|desc"
      }
      return sortParams
    },

    /**
     * getTableHeaders - Construct array of objects to show table headers
     *
     * @return {array}
     */
    getTableHeaders() {
      let headers = [
        {
          text: messages.label.TH_PROVIDER,
          align: "left",
          sortable: false,
          value: "title"
        },
        {
          text: messages.label.TH_ALTERNATE_NAMES,
          align: "left",
          value: "alternateNames",
          sortable: false
        },
        {
          text: messages.label.TH_PROVIDER_GROUP,
          align: "left",
          value: "providerGroupId",
          sortable: false
        }
      ]
      return headers
    },

    /**
     * retrieveProviderList - Retrieve all providers from API service
     * @return {Promise}  promise
     */
    retrieveProviderList() {
      this.providerListObject.range = this.getPageParams(this.pagination)
      this.providerListObject.sort = this.getSortParams(this.pagination)
      this.providerListObject.count = true
      // Apply provider groups filter
      let providerGroupIdArray = []
      if (this.selectedProviderGroups.length > 0) {
        this.$lodash.forEach(this.selectedProviderGroups, (providerGroup) => {
          providerGroupIdArray.push(providerGroup.id)
        })
        this.providerListObject.byProviderGroupId = providerGroupIdArray.join("|")
      } else {
        this.providerListObject = this.$lodash.omit(this.providerListObject, ["byProviderGroupId"])
      }
      // Always restrict to the sub accounts (i.e. provider groups)
      let accountIdsString = AppService.getSubAccountsIdsString(
        this.mpxSubAccounts
      )
      if (!this.$lodash.isEmpty(accountIdsString)) {
        this.providerListObject["byOwnerId"] = accountIdsString
      }
      this.providersLoading = true
      this.providersList = []
      providersDataService.retrieveProviders(
        this.providerListObject,
        this.VOD_SYCN_PREINGEST_DATA_SERVICE_URL).then((response) => {
          if (!this.$lodash.isUndefined(response.data.entries)) {
            this.providersList = response.data.entries
            this.addProviderGroupTitle()
            this.filterProviderList()
            this.totalItems = response.data.totalResults
          } else {
            this.providersList = []
            this.filteredProviderList = []
          }
          this.providersLoading = false
          return this.providersList
        }).catch((error) => {
          this.providersList = []
          this.filteredProviderList = []
          this.totalItems = 0
          this.providersLoading = false
          return Promise.reject(error)
        })
    },

    /**
    * Add provider group title based on provider group id
    */
    addProviderGroupTitle() {
      let providerObject
      for (let item in this.providersList) {
        providerObject = this.providersList[item]
        providerObject._providerGroupTitle = !this.$lodash.isNull(providerObject.providerGroupId)
          ? this.getProviderGroupTitle(providerObject.providerGroupId)
          : ""
      }
    },

    /**
    * Fiter the provider list based on selected provider group and search string
    */
    filterProviderList() {
      if (this.search) {
        this.search = this.search.toString().toLowerCase()
        const displayedKeys = [
          "title",
          "_providerGroupTitle",
          "alternateNames"
        ]
        this.filteredProviderList = this.providersList.filter((obj) => {
          let filteredObj = this.$lodash.pick(obj, displayedKeys)
          return Object.keys(filteredObj).some((key) => {
            if (key === "alternateNames") {
              if (filteredObj[key] !== null) {
                let keys = this.$lodash.keys(filteredObj[key])
                let values = this.$lodash.values(filteredObj[key])
                let isInKey = keys.some((key) => {
                  return key.toString()
                    .toLowerCase()
                    .includes(this.search)
                })
                let isInValue = values.some((value) => {
                  return value.toString()
                    .toLowerCase()
                    .includes(this.search)
                })
                let includes = isInKey || isInValue
                return includes
              }
            } else {
              if (filteredObj[key] !== null) {
                return filteredObj[key]
                  .toString()
                  .toLowerCase()
                  .includes(this.search)
              }
            }
          })
        })
      } else {
        this.filteredProviderList = this.providersList
      }
    },

    /**
     * Get providerGroup Title based on ID
     */
    getProviderGroupTitle(providerGroupId) {
      let providerGroupObject = {}
      if (this.providerGroups.length < 0) {
        this.GET_PROVIDER_GROUPS().then(() => {
          providerGroupObject = this.providerGroups.find(obj => obj.id === providerGroupId)
          if (providerGroupObject) {
            return providerGroupObject.title
          } else {
            return "N/A"
          }
        })
      } else {
        providerGroupObject = this.providerGroups.find(obj => obj.id === providerGroupId)
        if (providerGroupObject) {
          return providerGroupObject.title
        } else {
          return "N/A"
        }
      }
    },

    /**
     *
     */
    showCurrentProviderDetails(provider) {
      this.selected = provider
      ProvidersService.$emit("event:showProviderDetailsSheet", provider)
    },

    /**
     * Construct alternate names strings
     * Ex : key:value, key:value
     */
    getAlternateNamesString(alternateNames) {
      let altNamesStringArray = []
      let altNamesString = ""
      if (!this.$lodash.isEmpty(alternateNames)) {
        this.$lodash.forOwn(alternateNames, (value, key) => {
          altNamesStringArray.push(`${key} : ${value}`)
        })
        altNamesString = altNamesStringArray.join(", ")
      }
      return altNamesString
    }
  },
  created() {
    /**
     * Non reactive properties
     */
    this.messages = messages
    this.GET_PROVIDER_GROUPS()
    /**
     * Events
     */
    ProvidersService.$on("event:updateProviderList", this.retrieveProviderList)
    AppService.$on("event:mpxAccountChanged", this.reset)
  },
  beforeDestroy() {
    ProvidersService.$off("event:updateProviderList", this.retrieveProviderList)
    AppService.$off("event:mpxAccountChanged", this.reset)
  }
}
</script>

<style scoped>
</style>



// WEBPACK FOOTER //
// src/Providers/ProviderList/ProviderList.vue